import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";

import { getDashboard } from "@/api/dashboard";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ChurchTrendsCard } from "@/features/dashboard/components/ChurchTrendsCard";
import { ExecutiveSummary } from "@/features/dashboard/components/ExecutiveSummary";
import { MinistryBriefCard } from "@/features/dashboard/components/MinistryBriefCard";
import { MilestonesCard } from "@/features/dashboard/components/MilestonesCard";
import { OverviewQuickActions } from "@/features/dashboard/components/OverviewQuickActions";
import { PriorityCenterCard } from "@/features/dashboard/components/PriorityCenterCard";
import { RecentActivityCard } from "@/features/dashboard/components/RecentActivityCard";
import { OnboardingDashboard } from "@/features/dashboard/components/OnboardingDashboard";

export function DashboardPage() {
  const { churchId, user } = useAuth();
  const { permissions } = useEffectivePermissions();

  const dashboardQuery = useQuery({
    queryKey: ["dashboard", churchId],
    enabled: !!churchId,
    queryFn: () => getDashboard(churchId!),
  });

  if (dashboardQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <PageContainer>
        <QueryErrorState
          title="Unable to load overview"
          description="MinistryIQ could not load the latest church overview. Check your connection and try again."
          onRetry={() => void dashboardQuery.refetch()}
        />
      </PageContainer>
    );
  }

  const dashboard = dashboardQuery.data;

  if ("limited" in dashboard) {
    return <PageContainer>
      <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{dashboard.church_name} overview</h1>
      <p className="mt-2 text-sm text-[var(--text-secondary)]">Main Church information available to your role.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {dashboard.sections.map(section => <Card key={section.key} className="p-5">
          <h2 className="font-medium">{section.label}</h2>
          <p className="mt-2 text-3xl font-semibold">{section.value ?? "Not yet assessed"}</p>
          {section.to && <Link className="mt-3 inline-block text-indigo-500" to={section.to}>Open {section.label.toLowerCase()}</Link>}
        </Card>)}
      </div>
      {!dashboard.sections.length && <p className="mt-6">No Main Church overview sections are available. Use your ministry workspace for scoped access.</p>}
    </PageContainer>;
  }

  const firstName =
    user?.first_name?.trim() || "Pastor";

  if (dashboard.onboarding.maturity !== "ESTABLISHED") {
    return (
      <PageContainer>
        <OnboardingDashboard
          dashboard={dashboard}
          firstName={firstName}
          canConfigure={permissions.has("SETTINGS_MANAGE")}
          userId={user?.id ?? "unknown"}
          churchId={churchId ?? "unknown"}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-indigo-950/30 px-6 py-6 shadow-xl shadow-black/20 sm:px-7">
        <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute -bottom-28 left-24 h-56 w-56 rounded-full bg-violet-500/5 blur-3xl" />

        <div className="relative">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-400">
              Overview
            </p>

            {dashboardQuery.isFetching && (
              <p
                role="status"
                className="text-xs font-medium text-indigo-300"
              >
                Refreshing overview...
              </p>
            )}
          </div>

          <h1 className="mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {getGreeting()}, {firstName}

            <span
              aria-hidden="true"
              className="ml-2 inline-block origin-bottom-right text-2xl transition hover:rotate-12"
            >
              👋
            </span>
          </h1>

          <p className="mt-2 text-sm font-medium text-slate-400">
            {getCurrentDate()}
          </p>

          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
            Here&apos;s what&apos;s happening in your church today.
          </p>
        </div>
      </section>

      <div className="mt-7">
        <ExecutiveSummary dashboard={dashboard} />
      </div>

      <div className="mt-7 grid items-start gap-6 xl:grid-cols-[1fr_1fr_0.95fr]">
        <div className="self-start">
          <MinistryBriefCard
            items={dashboard.ministry_brief}
          />
        </div>

        <div className="self-start">
          <PriorityCenterCard
            priorities={dashboard.priorities}
          />
        </div>

        <div className="self-start">
          <RecentActivityCard
            events={dashboard.recent_events.slice(0, 6)}
          />
        </div>
      </div>

      <div className="mt-7 grid items-start gap-6 xl:grid-cols-[1.2fr_0.9fr_0.9fr]">
        <ChurchTrendsCard
          title="Attendance Trend"
          subtitle="Weekly attendance over the last eight weeks"
          data={dashboard.attendance_trend}
          dataKeyLabel="Attendance"
        />

        <MilestonesCard
          milestones={dashboard.milestones}
        />

        <OverviewQuickActions />
      </div>
    </PageContainer>
  );
}

function getGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Good Morning";
  }

  if (hour < 17) {
    return "Good Afternoon";
  }

  return "Good Evening";
}

function getCurrentDate() {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
